import { useState, useEffect, useCallback } from 'react';
import { apiGetSuggestionList, apiPutSuggestionList } from '../api';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';
import { useWriteGate } from '../lib/writeGate';

// Global, editable, app-wide suggestion lists (src/routes/settings.js): backline
// types, diet types… Loads once; put() persists and updates local state.
export function useSuggestionList(list: string) {
  // Capability-aware write gate (lot A2). `PUT /api/settings/:list` is mounted
  // AFTER the server's global read-only guard (server/index.js:591) → 403 for a
  // `users.role = 'read-only'` account. These lists are app-wide (NOT
  // band-scoped), so the gate falls back to the active band — there is no entity
  // band to pass, and the write is refused for a read-only account either way.
  // No NEW hook is added to the call sites: the freeze lives in `put` itself.
  const writeGate = useWriteGate();
  const [types, setTypes] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const t = await apiGetSuggestionList(list);
      setTypes(t);
    } catch (e: any) {
      reportSaveError('settings', e);
    } finally {
      setLoaded(true);
    }
  }, [list]);
  useEffect(() => { void load(); }, [load]);

  const put = useCallback(async (next: string[]) => {
    // Frozen for a global read-only account (server 403s): return the UNCHANGED
    // server-known list, never the value we could not persist. All call sites
    // `void put(...)` and ignore the return; local state is left untouched.
    if (!writeGate.canWrite) return types;
    try {
      const saved = await apiPutSuggestionList(list, next);
      setTypes(saved);
      reportSaveOk('settings');
      return saved;
    } catch (e: any) {
      reportSaveError('settings', e);
      return next;
    }
  }, [list, writeGate.canWrite, types]);

  return { types, loaded, put };
}

// Convenience wrapper for the backline type suggestions.
export function useBacklineTypes() {
  return useSuggestionList('backline-types');
}
